/**
 * Removes anything that looks like a credential from a value about to be logged.
 *
 * <h2>Why this exists rather than "be careful at the call site"</h2>
 *
 * Being careful failed. `LoginPage` contained
 * `console.log('Google Sign-In response:', response)` where `response.token` was the
 * credential, so every social sign-in wrote an auth token into the browser console —
 * readable by any extension, and by anybody the person later hands their laptop to.
 * It survived because the whole object was logged, and the interesting field was two
 * levels down where nobody was looking.
 *
 * <p>That is the shape this catches: not somebody typing `log(token)`, which is
 * obvious, but somebody logging a response, an error, or a request that happens to
 * carry one. A rule the author has to remember does not survive the first tired
 * afternoon; a function every log line already passes through does.
 *
 * <h2>What is redacted, and why by key rather than by value</h2>
 *
 * Keys are matched, not values. A token is a string of letters and digits and so is
 * a company name, so any value-shaped heuristic either misses real secrets or
 * mangles ordinary text — and a logger that quietly corrupts messages is worse than
 * one that leaks, because nobody notices it happening.
 *
 * <p>A key match is unambiguous: a field called `password` holds a password. The
 * cost is that a secret in a field called something else gets through, which is
 * accepted — this narrows the blast radius, it does not seal it.
 */

/**
 * Field names whose values never appear in a log.
 *
 * <p>Matched case-insensitively as substrings, so `access_token`, `refreshToken`,
 * `idToken` and `X-Auth-Token` are all covered by `token`. Substring matching is
 * deliberately blunt: a false positive redacts something harmless, a false negative
 * publishes a credential, and those are not equally bad.
 */
const SECRET_KEYS = [
  'token',
  'password',
  'secret',
  'credential',
  'authorization',
  'auth',
  'jwt',
  'apikey',
  'api_key',
  'sessionid',
  'cookie',
  'signature',
];

/** What replaces a redacted value. Says what happened rather than leaving a gap. */
const REDACTED = '[redacted]';

/**
 * How deep to walk. An axios error holds its request, which holds its config, which
 * holds the whole thing again — deep enough to matter, and cyclic. The cycle guard
 * below handles the loop; this bounds the work.
 */
const MAX_DEPTH = 6;

function isSecretKey(key: string): boolean {
  const lowered = key.toLowerCase();
  return SECRET_KEYS.some((secret) => lowered.includes(secret));
}

/**
 * Returns a copy of the value with every secret-looking field replaced.
 *
 * <p>A copy, never a mutation: the object being logged is usually still in use by
 * the code that logged it, and scrubbing it in place would delete a token the
 * application is about to send.
 */
export function redact(value: unknown): unknown {
  return walk(value, 0, new WeakSet());
}

function walk(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return '[deep]';
  }

  // Cycles are normal here, not exotic: an axios error's config references the error.
  // Without this the walk never returns and the tab freezes -- a logger that hangs
  // the application is worse than the leak it was added to prevent.
  if (seen.has(value)) {
    return '[circular]';
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => walk(item, depth + 1, seen));
  }

  // An Error is an object whose useful parts are non-enumerable, so a spread of one
  // produces {}. Its message and stack are lifted out explicitly, and the message is
  // scrubbed as text because a thrown string can carry anything.
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    result[key] = isSecretKey(key) ? REDACTED : walk(item, depth + 1, seen);
  }
  return result;
}
